import { AxiosError } from "axios";
import { toast } from "sonner";

import { clearAuthCookies } from "../auth/cookies";
import { dispatchAccountDeleted } from "../auth/account-deleted";

declare module "axios" {
  interface AxiosRequestConfig {
    skipErrorToast?: boolean;
  }
}

export interface ErrorEnvelope {
  code?: string;
  message?: string;
  retry_after?: number;
  fields?: Record<string, string[]>;
  data?: Record<string, unknown>;
}

export interface ErrorResponse {
  ok?: boolean;
  data?: unknown;
  error?: ErrorEnvelope;
  errors?: Record<string, string[]>;
  message?: string;
  status?: number;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly retryAfter?: number;
  public readonly errors?: Record<string, string[]>;
  public readonly data?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code?: string,
    retryAfter?: number,
    errors?: Record<string, string[]>,
    data?: Record<string, unknown>,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.errors = errors;
    this.data = data;
    this.name = "ApiError";
  }
}

const redirectToLogin = (): void => {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/auth")) return;
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/auth?redirect=${redirect}`;
};

export const handleAxiosError = (error: AxiosError<ErrorResponse>): never => {
  const status = error.response?.status;
  const data = error.response?.data;
  const envelope = data?.error ?? {};
  const message = envelope.message ?? data?.message;
  const code = envelope.code;
  const retryAfter = envelope.retry_after;
  const errors = envelope.fields ?? data?.errors;
  const errorData = envelope.data;

  if (!error.config?.skipErrorToast) {
    switch (status) {
      case 401:
        clearAuthCookies();
        if (code === "ACCOUNT_DELETED") {
          dispatchAccountDeleted();
          break;
        }
        toast.error("خطای احراز هویت", {
          id: "api-auth-401",
          description: "نشست شما منقضی شده است. لطفاً مجدداً وارد شوید.",
        });
        if (error.config?.headers?.Authorization) {
          redirectToLogin();
        }
        break;

      case 403:
        toast.error("خطای دسترسی", {
          id: `api-403-${message ?? ""}`,
          description: message || "شما دسترسی لازم برای این عملیات را ندارید",
        });
        break;

      case 404:
        break;

      case 413:
        toast.error("حجم فایل ارسالی زیاد است", {
          id: "api-413",
          description:
            "حجم فایل انتخابی از سقف مجاز سرور بیشتر است؛ فایل کوچک‌تری انتخاب کنید.",
        });
        break;

      case 422:
        toast.error("خطای اعتبارسنجی", {
          id: `api-422-${message ?? ""}`,
          description: message || "لطفاً ورودیهای خود را بررسی کنید",
        });
        break;

      case 429:
        toast.error("درخواست بیش از حد", {
          id: "api-429",
          description: message || "کمی صبر کنید و دوباره تلاش کنید",
        });
        break;

      case 500:
        toast.error("خطای سرور", {
          id: code ? `api-500-${code}` : "api-500",
          description: code
            ? message || "متأسفانه مشکلی در سرور رخ داده است. لطفاً دوباره تلاش کنید"
            : "متأسفانه مشکلی در سرور رخ داده است. لطفاً دوباره تلاش کنید",
        });
        break;

      default:
        toast.error("خطا", {
          id: `api-${status ?? "unknown"}-${message ?? ""}`,
          description: message || "متأسفانه مشکلی پیش آمده است",
        });
    }
  }

  throw new ApiError(message || "خطای سرور", status || 500, code, retryAfter, errors, errorData);
};
